import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileSpreadsheet, Upload, Loader2, CheckCircle, AlertCircle, Users, TrendingUp, FileDown, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupplierPriceAnalysis = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file => 
      file.name.endsWith('.xlsx') || file.name.endsWith('.xls')
    );
    
    if (validFiles.length !== acceptedFiles.length) {
      toast.error('بعض الملفات غير مدعومة - يجب رفع ملفات Excel فقط');
    }
    
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      setResult(null);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: true
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('الرجاء اختيار ملف واحد على الأقل');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_URL}/api/analysis/supplier-comparison/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        toast.success(`تم تحليل ${data.total_records} سجل من ${data.suppliers_count} مورد`);
      } else {
        throw new Error(data.detail || 'فشل في تحليل الملفات');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    if (!result?.task_id) {
      toast.error('الرجاء رفع وتحليل الملفات أولاً');
      return;
    }

    setExporting(true);

    try {
      const response = await fetch(`${API_URL}/api/analysis/supplier-comparison/export-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ task_id: result.task_id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'فشل في تصدير الملف');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `مقارنة_الموردين_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('تم تصدير التقرير بنجاح');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFiles([]);
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6" dir="rtl">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 mb-4 shadow-lg shadow-purple-500/25">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">تحليل الأسعار للموردين</h1>
          <p className="text-slate-400">رفع ملفات Excel لمقارنة أسعار عدة موردين</p>
        </div>

        {/* Formula Info */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 mb-6 border border-slate-700">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <TrendingUp className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">مقارنة أسعار الموردين</h3>
              <p className="text-slate-400 text-sm">
                ارفع ملفات Excel لكل مورد أو ملف واحد يحتوي على بيانات عدة موردين
              </p>
              <p className="text-slate-500 text-xs mt-1">
                * سيتم مقارنة الأصناف المتشابهة حسب كود الصنف والتاريخ
              </p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 mb-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
              isDragActive
                ? 'border-purple-500 bg-purple-500/10'
                : files.length > 0
                ? 'border-purple-500/50 bg-purple-500/5'
                : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/30'
            }`}
          >
            <input {...getInputProps()} data-testid="supplier-file-input" />
            
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-xl bg-slate-700 flex items-center justify-center mb-3">
                <Upload className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-white font-medium mb-1">
                {isDragActive ? 'أفلت الملفات هنا' : 'اسحب ملفات Excel هنا'}
              </p>
              <p className="text-slate-400 text-sm">أو اضغط لاختيار ملفات</p>
              <p className="text-slate-500 text-xs mt-2">يدعم رفع عدة ملفات (.xlsx و .xls)</p>
            </div>
          </div>

          {/* Files List */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-slate-400 text-sm mb-2">الملفات المختارة ({files.length}):</p>
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-slate-700/50 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-white text-sm">{file.name}</p>
                      <p className="text-slate-500 text-xs">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-slate-600 rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
              ))}
              
              <button
                onClick={resetForm}
                className="text-sm text-slate-400 hover:text-white transition-colors mt-2"
              >
                إزالة جميع الملفات
              </button>
            </div>
          )}

          {/* Upload Button */}
          {files.length > 0 && !result && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full mt-4 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="supplier-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري التحليل...
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  تحليل ومقارنة الموردين
                </>
              )}
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">خطأ في التحليل</p>
              <p className="text-red-300/70 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 space-y-6">
            {/* Success Header */}
            <div className="flex items-center gap-3 pb-4 border-b border-slate-700">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium">تم التحليل بنجاح</p>
                <p className="text-slate-400 text-sm">الملف جاهز للتصدير</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{result.total_records}</p>
                <p className="text-slate-400 text-sm">إجمالي السجلات</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-purple-400">{result.unique_items}</p>
                <p className="text-slate-400 text-sm">عدد الأصناف</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-pink-400">{result.suppliers_count}</p>
                <p className="text-slate-400 text-sm">عدد الموردين</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{result.common_items || 0}</p>
                <p className="text-slate-400 text-sm">الأصناف المشتركة</p>
              </div>
            </div>

            {/* Suppliers List */}
            {result.suppliers && result.suppliers.length > 0 && (
              <div>
                <p className="text-slate-400 text-sm mb-2">الموردين:</p>
                <div className="flex flex-wrap gap-2">
                  {result.suppliers.map((supplier, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-sm"
                    >
                      {supplier}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Report Sheets Info */}
            <div className="bg-slate-700/30 rounded-xl p-4">
              <p className="text-white font-medium mb-3">أوراق التقرير (18 ورقة):</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {[
                  { name: 'البيانات الموحدة', icon: '📊' },
                  { name: 'الأصناف المشتركة', icon: '🔗' },
                  { name: 'مقارنة أسعار الموردين', icon: '⚖️' },
                  { name: 'أفضل مورد لكل صنف', icon: '🏆' },
                  { name: 'المقارنة الزمنية', icon: '📆' },
                  { name: 'ملخص الموردين', icon: '📋' },
                  { name: 'الوفر المحتمل', icon: '💵', highlight: true },
                  { name: 'تنبيهات الأسعار', icon: '⚠️', highlight: true },
                  { name: 'اتجاه الأسعار', icon: '📈', highlight: true },
                  { name: 'تحليل موسمي', icon: '📅', highlight: true },
                  { name: 'تصنيف الموردين', icon: '🏅', highlight: true },
                  { name: 'تكرار الشراء', icon: '🔄', highlight: true },
                  { name: 'توصيات الشراء', icon: '💡', highlight: true },
                  { name: 'تحليل القيمة', icon: '💰', highlight: true },
                  { name: 'لوحة تحكم تنفيذية', icon: '🎯', highlight: true },
                  { name: 'مؤشرات الأداء KPIs', icon: '📊', highlight: true },
                  { name: 'تحليل المخاطر', icon: '🛡️', highlight: true },
                  { name: 'ملخص عام', icon: '📋' }
                ].map((sheet, idx) => (
                  <div key={idx} className={`flex items-center gap-2 ${sheet.highlight ? 'text-purple-300' : 'text-slate-300'}`}>
                    <span>{sheet.icon}</span>
                    <span className="text-xs">{sheet.name}</span>
                    {sheet.highlight && <span className="text-xs text-pink-400">جديد</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={exporting}
              className="w-full py-4 px-4 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
              data-testid="supplier-export-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  جاري التصدير...
                </>
              ) : (
                <>
                  <FileDown className="w-6 h-6" />
                  تصدير تقرير مقارنة الموردين
                </>
              )}
            </button>

            {/* Info Note */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-purple-300">التقرير يحتوي على مقارنة شاملة بين الموردين</p>
                <p className="text-purple-300/70 mt-1">
                  يتم مطابقة الأصناف حسب كود الصنف والتاريخ لتحديد أفضل مورد
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-slate-500 text-sm">
          تطوير محمد مهدي
        </div>
      </div>
    </div>
  );
};

export default SupplierPriceAnalysis;
